import React, { memo, useEffect, useState } from 'react';
import { Autocomplete, ListItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRequestdata } from '../../application/Request/useRequestData';
import useRuleValidation from '../../shared/hooks/useRuleValidation';
import { Country, Locality, State } from '../../models/City';
import CustomArrowIcon from '../CustomArrowIcon/CustomArrowIcon';

interface LocalitiesProps {
  locality?: any;
  onChange?: (value: any) => void;
  disabled?: boolean;
  checkLoadingData?: (isLoading: boolean) => React.ReactNode;
}

const Localities: React.FC<LocalitiesProps> = ({ locality, onChange = (value) => { console.log(value); }, disabled = false, checkLoadingData }) => {
  const [city, setCity] = useState < Locality | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<Locality[]>([]);
  const { t } = useTranslation();
  const { localities, onFetchLocalities } = useRequestdata({});
  const cityRule = useRuleValidation(() => !!city);

  useEffect(() => {
    if (!localities.loaded && !locality) {
      onFetchLocalities();
    }
  }, [locality]);

  useEffect(() => {
    if (locality) {
      setCity(locality);
    }

  }, [locality?.id]);

  useEffect(() => {
    if (!city) {
      onChange(null);
      return;
    }

    const fullCity = localities.cities.find((c) => c.id === city.id) || city;
    const state = localities.states.find((state: State) => state.id === fullCity.state_id) || null
    const country = state ? (localities.countries.find((country: Country) => country.id === state.country_id) || null) : null

    const locality = {
      ...city,
      state: {
        ...state,
        country
      }
    };
    onChange(locality);
  }, [city]);

  useEffect(() => {
    if (!inputValue) {
      setFilteredOptions([]);
      return;
    }
    const normalizedInput = inputValue.toLowerCase();
    const result = localities.cities.filter((c: Locality) => c.name.toLowerCase().includes(normalizedInput)).slice(0, 50);

    setFilteredOptions(result);
  }, [inputValue, localities.cities]);

  const getInputProps = (params: any, rule: any, tabIndex: number) => {
    return {
      ...params,
      inputProps: {
        ...params.inputProps,
        tabIndex,
        autoComplete: 'new-password',
        'data-testid': 'city-input',
      },
      InputProps: {
        ...params.InputProps,
        endAdornment: <>{checkLoadingData ? checkLoadingData(rule.adornment) : null}{params.InputProps.endAdornment}</>
      }
    };
  };

  const renderOption = (props: any, option: Locality) => {
    const state = localities.states.find(s => s.id === option.state_id);
    
    return (
      <ListItem {...props} key={option?.id}>
        {option.name}{state ? `, ${state.name}` : ''}
      </ListItem>
    );
  };

  const autocompleteProps = {
    fullWidth: true,
    size: 'small' as const,
    renderOption,
    getOptionLabel: (option: Locality) => {
      if (!option) return '';
      const state = localities.states.find(s => s.id === option.state_id);
      return state ? `${option.name}, ${state.name}` : option.name;
    },
  };

  return (
      <Autocomplete
        {...autocompleteProps}
        options={filteredOptions}
        popupIcon={<CustomArrowIcon testId="city-select-arrow" />}
        value={city}
        disabled={disabled}
        onInputChange={(e, value) => setInputValue(value)} 
        onChange={(e, value) => setCity(value)}
        renderInput={(params) => <TextField autoComplete={false} {...getInputProps(params, cityRule, 12)} label={t('city')} />}
      />
  );
};

export default memo(Localities);
